import { AABB, DuoMeasurementValues, View } from "@novorender/api";
import { ReadonlyVec3, vec3 } from "gl-matrix";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    HighlightCollection,
    highlightCollectionsActions,
    useDispatchHighlightCollections,
} from "contexts/highlightCollections";
import { ObjectVisibility, Picker, renderActions, selectPicker } from "features/render";
import useFlyTo from "hooks/useFlyTo";
import { explorerActions } from "slices/explorer";
import { telemetry } from "telemetry";
import { isDebugEnabled, log } from "utils/log";

import { measureActions, selectObjectMeasureIds, selectObjectMeasureResult } from "./measureSlice";

const l = log.measure.objectToObject;

/**
 * The search gets slower the further apart the objects are, so try a capped search first and only
 * fall back to an uncapped one if it finds nothing. The user picked these two objects deliberately,
 * so a distance beyond the cap is still a valid answer - it just costs more to find.
 */
const MAX_DISTANCE_METERS = 100;

/** Stable empty array, so that clearing the highlight does not re-fire the effect every render. */
const NO_IDS: number[] = [];

/**
 * Drives {@link Picker.ObjectMeasurement}: once two objects are picked, measures the shortest line
 * between them and highlights the picks. The result survives leaving the tool - like the other
 * measure tools, it is kept on save and only reverted when the user cancels out of the mode, which
 * the edit-session snapshot in `measureSlice` takes care of.
 */
export function useObjectToObjectMeasure() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const picker = useAppSelector(selectPicker);
    const objectIds = useAppSelector(selectObjectMeasureIds);
    const { t } = useTranslation();

    useFitMeasurementInView();
    useXrayMeasuredObjects();

    // Tinting a pick is an affordance for the picking itself, so it stops with the tool, and stops
    // once the pair is complete - from then on the two objects are shown in their own right, by
    // `useXrayMeasuredObjects`.
    const highlightIds = picker === Picker.ObjectMeasurement && objectIds.length < 2 ? objectIds : NO_IDS;

    // The secondary highlight is shared with other features, so only clear it if we put something
    // there ourselves - otherwise mounting this hook would wipe e.g. search result highlighting.
    const ownsHighlight = useRef(false);
    useEffect(() => {
        if (highlightIds.length > 0) {
            ownsHighlight.current = true;
        } else if (!ownsHighlight.current) {
            return;
        } else {
            ownsHighlight.current = false;
        }

        dispatchHighlightCollections(
            highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, highlightIds),
        );
    }, [highlightIds, dispatchHighlightCollections]);

    useEffect(() => {
        if (objectIds.length < 2) {
            return;
        }

        let aborted = false;
        measure();
        return () => {
            aborted = true;
        };

        async function measure() {
            const [a, b] = objectIds;
            const measureView = view?.measure;
            if (!measureView || aborted) {
                return;
            }

            dispatch(measureActions.setObjectMeasureLoading(true));
            try {
                const start = performance.now();
                let result = await measureView.core.measureObjectToObject(a, b, {
                    maxDistance: MAX_DISTANCE_METERS,
                });
                l.debug(
                    `Measured ${a} to ${b} within ${MAX_DISTANCE_METERS}m in ${Math.round(
                        performance.now() - start,
                    )}ms:`,
                    result,
                );

                if (!result) {
                    const uncappedStart = performance.now();
                    result = await measureView.core.measureObjectToObject(a, b);
                    l.debug(
                        `Retried ${a} to ${b} uncapped in ${Math.round(performance.now() - uncappedStart)}ms:`,
                        result,
                    );
                }

                if (aborted) {
                    return;
                }

                await logPickedObjectExtents(view, a, b, result);

                dispatch(measureActions.setObjectMeasureResult(result));
                if (!result) {
                    // Both objects need parametric data. Report that separately, because it means
                    // these objects can never be measured rather than that nothing was found.
                    const [productA, productB] = await Promise.all([
                        measureView.core.getParametricProduct(a).catch(() => undefined),
                        measureView.core.getParametricProduct(b).catch(() => undefined),
                    ]);
                    l.warn(
                        `No connection between ${a} and ${b}. Parametric data:`,
                        `${a}=${Boolean(productA)}`,
                        `${b}=${Boolean(productB)}`,
                    );

                    if (aborted) {
                        return;
                    }

                    dispatch(
                        explorerActions.setSnackbarMessage({
                            msg: productA && productB ? t("objectMeasureNoResult") : t("objectMeasureNoParametricData"),
                            closeAfter: 5000,
                        }),
                    );
                }
            } catch (e) {
                l.error(`Failed to measure ${a} to ${b}:`, e);
                console.warn(e);
                telemetry.trackCaughtException({
                    source: "measure.useObjectToObjectMeasure",
                    category: "render",
                    error: e,
                });
                if (!aborted) {
                    dispatch(measureActions.setObjectMeasureResult(undefined));
                    dispatch(explorerActions.setSnackbarMessage({ msg: t("objectMeasureNoResult"), closeAfter: 5000 }));
                }
            }
        }
    }, [view, objectIds, dispatch, t]);
}

/**
 * Once both objects are picked, x-rays the model and puts the pair into a highlight collection that
 * renders them with their default appearance, so the thing being measured stands out of the
 * surrounding geometry. The x-ray mode is left on afterwards for the user to turn off, the same as
 * if they had switched it on themselves.
 */
function useXrayMeasuredObjects() {
    const dispatch = useAppDispatch();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const objectIds = useAppSelector(selectObjectMeasureIds);

    const measuredIds = objectIds.length === 2 ? objectIds : NO_IDS;
    const ownsCollection = useRef(false);

    useEffect(() => {
        if (measuredIds.length > 0) {
            ownsCollection.current = true;
            dispatch(renderActions.setVisibilityMode(ObjectVisibility.Xray));
        } else if (!ownsCollection.current) {
            return;
        } else {
            ownsCollection.current = false;
        }

        dispatchHighlightCollections(
            highlightCollectionsActions.setIds(HighlightCollection.ObjectMeasure, measuredIds),
        );
    }, [measuredIds, dispatch, dispatchHighlightCollections]);
}

/** Keeps the measured line off the edges of the viewport instead of spanning it exactly. */
const FIT_PADDING = 1.4;

/**
 * Touching objects measure to nearly nothing, and fitting that would put the camera inside the
 * geometry, so never frame anything tighter than this, in meters.
 */
const MIN_FIT_RADIUS_METERS = 1;

/**
 * Frames a finished measurement: keeping its current orientation, the camera moves to centre the
 * measured line and pulls back far enough to fit it in the frustum. Runs once per result, so the
 * user is free to navigate away afterwards without being pulled back.
 */
function useFitMeasurementInView() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const result = useAppSelector(selectObjectMeasureResult);
    const flyTo = useFlyTo();
    const fitted = useRef<DuoMeasurementValues | undefined>(undefined);

    useEffect(() => {
        if (!result || !view || result === fitted.current) {
            return;
        }
        fitted.current = result;

        const a = result.measureInfoA?.point;
        const b = result.measureInfoB?.point;
        if (!a || !b) {
            return;
        }

        const radius = Math.max((vec3.distance(a, b) / 2) * FIT_PADDING, MIN_FIT_RADIUS_METERS);
        // Both camera types fit the sphere against the vertical field of view, so a portrait
        // viewport needs a larger sphere to keep a horizontal line from running off the sides.
        const { width, height } = view.renderState.output;
        const aspect = width > 0 && height > 0 ? width / height : 1;

        void flyTo({
            sphere: {
                center: vec3.lerp(vec3.create(), a, b, 0.5),
                radius: aspect < 1 ? radius / aspect : radius,
            },
        });
    }, [result, view, flyTo]);
}

/** Slack when testing a measured point against a bounding box, in meters. */
const BOUNDS_EPSILON = 0.01;

function isInsideBounds(point: ReadonlyVec3, box: AABB) {
    for (let i = 0; i < 3; ++i) {
        if (point[i] < box.min[i] - BOUNDS_EPSILON || point[i] > box.max[i] + BOUNDS_EPSILON) {
            return false;
        }
    }
    return true;
}

/**
 * Logs what was actually picked and where the measured points landed, to tell a mis-pick apart from
 * a bad measurement: a closest point outside its own object's bounding box can only be the latter.
 * Only runs when the log namespace is enabled, since it costs a metadata lookup per object.
 */
async function logPickedObjectExtents(
    view: View | undefined,
    a: number,
    b: number,
    result: DuoMeasurementValues | undefined,
) {
    if (!view?.data || !isDebugEnabled(l.namespace)) {
        return;
    }

    const objects = await Promise.all(
        [
            { id: a, point: result?.measureInfoA?.point },
            { id: b, point: result?.measureInfoB?.point },
        ].map(async ({ id, point }) => {
            try {
                const { name, path, bounds } = await view.data!.getObjectMetaData(id);
                return { id, name, path, box: bounds?.box, point };
            } catch (e) {
                l.warn(`Could not read metadata for ${id}:`, e);
                return { id, point };
            }
        }),
    );

    l.debug("Picked objects:", objects);

    for (const { id, box, point } of objects) {
        if (box && point && !isInsideBounds(point, box)) {
            l.warn(
                `Measured point on ${id} is outside the object's own bounds - the measurement is wrong, not the pick.`,
                { point, box },
            );
        }
    }
}
